import { useEffect, useState } from "react";
import { createPortal } from "react-dom";
import { useSelector } from "react-redux";
import { AnimatePresence, motion } from "framer-motion";
import "bootstrap-icons/font/bootstrap-icons.css";
import logo from "../../assets/echovibe.png";
import LoginForm from "./LoginForm";
import RegisterForm from "./RegisterForm";
import ActivateForm from "./ActivateForm";
import ProfileCreateWizard from "../profile/create/ProfileCreateWizard";
import { needsBusinessProfileSetup } from "../../utils/resolvePostAuthPath";

const AUTH_VIEWS = {
    LOGIN: "login",
    REGISTER: "register",
    ACTIVATE: "activate",
    PROFILE: "profile",
};

const APPEAR_DURATION = 0.5;

const backdropVariants = {
    hidden: { opacity: 0 },
    visible: {
        opacity: 1,
        transition: { duration: APPEAR_DURATION, ease: "easeOut" },
    },
};

const panelVariants = {
    hidden: { opacity: 0, y: 28, scale: 0.96 },
    visible: {
        opacity: 1,
        y: 0,
        scale: 1,
        transition: { duration: APPEAR_DURATION, ease: [0.22, 1, 0.36, 1] },
    },
};
export default function AuthModal({
    open = true,
    redirectPath = null,
    onComplete = null,
    onClose = null,
    initialTab = AUTH_VIEWS.LOGIN,
    title = "Sign in to continue",
    description = "Log in or create an account to keep going.",
}) {
    const user = useSelector((state) => state.auth.user);
    const [activeView, setActiveView] = useState(initialTab);
    const [activationEmail, setActivationEmail] = useState("");
    const [wizardFooter, setWizardFooter] = useState(null);

    useEffect(() => {
        if (open) {
            setActiveView(initialTab);
            setActivationEmail("");
            setWizardFooter(null);
        }
    }, [open, initialTab]);

    useEffect(() => {
        if (activeView !== AUTH_VIEWS.PROFILE) {
            setWizardFooter(null);
        }
    }, [activeView]);

    useEffect(() => {
        if (!open) return undefined;

        const previousBodyOverflow = document.body.style.overflow;
        document.body.style.overflow = "hidden";

        const handleKeyDown = (event) => {
            if (event.key === "Escape") onClose?.();
        };

        window.addEventListener("keydown", handleKeyDown);

        return () => {
            document.body.style.overflow = previousBodyOverflow;
            window.removeEventListener("keydown", handleKeyDown);
        };
    }, [open, onClose]);

    const handleActivationRequired = (email) => {
        setActivationEmail(email);
        setActiveView(AUTH_VIEWS.ACTIVATE);
    };

    const handleAuthSuccess = async () => {
        const needsProfile = await needsBusinessProfileSetup();
        if (needsProfile) {
            setActiveView(AUTH_VIEWS.PROFILE);
            return;
        }
        onComplete?.();
    };

    const handleProfileComplete = () => {
        onComplete?.();
    };

    const isProfileView = activeView === AUTH_VIEWS.PROFILE;
    const isProfileOnboarding =
        initialTab === AUTH_VIEWS.PROFILE || isProfileView;

    useEffect(() => {
        if (open && isProfileOnboarding && !user) {
            onClose?.();
        }
    }, [open, isProfileOnboarding, user, onClose]);

    const displayTitle =
        activeView === AUTH_VIEWS.ACTIVATE
            ? "Activate your account"
            : isProfileView
              ? "Set up your business"
              : title;
    const displayDescription =
        activeView === AUTH_VIEWS.ACTIVATE
            ? "Check your email for a 6-digit activation code (expires in 10 minutes)."
            : isProfileView
              ? "A few quick steps to personalize your experience."
              : description;

    if (!open) return null;
    if (isProfileOnboarding && !user) return null;

    const handleClose = () => {
        onClose?.();
    };

    return createPortal(
        <div
            className="fixed inset-0 z-[200] overflow-y-auto"
            role="dialog"
            aria-modal="true"
            aria-labelledby="auth-modal-title"
        >
            <motion.div
                className="fixed inset-0 bg-slate-900/40 backdrop-blur-sm"
                aria-hidden
                initial="hidden"
                animate="visible"
                variants={backdropVariants}
                onClick={handleClose}
            />

            <div className="relative z-10 flex min-h-full items-center justify-center p-4">
            <motion.div
                className={`relative w-full max-h-[calc(100dvh-2rem)] flex flex-col overflow-hidden rounded-2xl border border-white/20 bg-white/95 shadow-2xl backdrop-blur-md ${
                    isProfileView ? "max-w-2xl" : "max-w-md"
                }`}
                initial="hidden"
                animate="visible"
                variants={panelVariants}
                onClick={(event) => event.stopPropagation()}
            >
                <div
                    className={`shrink-0 ${isProfileView ? "p-4 pb-0 md:p-5" : "p-6 pb-0 md:p-8 md:pb-0"}`}
                >
                <button
                    type="button"
                    className="absolute right-3 top-3 rounded-md p-1.5 text-slate-500 transition hover:bg-slate-100 hover:text-slate-800 md:right-4 md:top-4"
                    aria-label="Close"
                    onClick={handleClose}
                >
                    <i className="bi bi-x-lg text-sm" aria-hidden />
                </button>

                <div className={isProfileView ? "mb-3 flex justify-center" : "mb-5 flex justify-center"}>
                    <img
                        src={logo}
                        alt="EzePulse"
                        className={isProfileView ? "h-8 w-auto" : "h-9 w-auto md:h-10"}
                    />
                </div>

                <div className={isProfileView ? "mb-3 text-center" : "mb-5 text-center"}>
                    <h2
                        id="auth-modal-title"
                        className={
                            isProfileView
                                ? "mb-0.5 text-lg font-semibold text-slate-900"
                                : "mb-1 text-xl font-semibold text-slate-900"
                        }
                    >
                        {displayTitle}
                    </h2>
                    <p className={isProfileView ? "mb-0 text-xs text-slate-600" : "mb-0 text-sm text-slate-600"}>
                        {displayDescription}
                    </p>
                </div>
                </div>

                {/* Tab switch hidden — use "Already have an account?" / "Do not have an account?" below the form */}
                {/* <div className="mb-5 grid grid-cols-2 gap-1 rounded-xl bg-slate-100 p-1">
                    <button
                        type="button"
                        className={`rounded-lg px-3 py-2 text-sm font-semibold transition ${
                            activeTab === AUTH_TABS.LOGIN
                                ? "bg-white text-slate-900 shadow-sm"
                                : "text-slate-600 hover:text-slate-900"
                        }`}
                        onClick={() => setActiveTab(AUTH_TABS.LOGIN)}
                    >
                        Login
                    </button>
                    <button
                        type="button"
                        className={`rounded-lg px-3 py-2 text-sm font-semibold transition ${
                            activeTab === AUTH_TABS.REGISTER
                                ? "bg-white text-slate-900 shadow-sm"
                                : "text-slate-600 hover:text-slate-900"
                        }`}
                        onClick={() => setActiveTab(AUTH_TABS.REGISTER)}
                    >
                        Register
                    </button>
                </div> */}

                <div
                    className={`min-h-0 flex-1 overflow-y-auto overscroll-contain px-6 md:px-8 ${
                        wizardFooter ? "pb-4" : "pb-6 md:pb-8"
                    }`}
                >
                <div className={isProfileView ? "px-0.5" : "min-h-[28rem] px-0.5"}>
                    <AnimatePresence mode="wait" initial={false}>
                        {activeView === AUTH_VIEWS.LOGIN ? (
                            <motion.div
                                key="login"
                                initial={{ opacity: 0 }}
                                animate={{ opacity: 1 }}
                                exit={{ opacity: 0 }}
                                transition={{ duration: 0.25 }}
                            >
                                <LoginForm
                                    redirectPath={redirectPath}
                                    onAuthSuccess={handleAuthSuccess}
                                    onSwitchToRegister={() => setActiveView(AUTH_VIEWS.REGISTER)}
                                    onActivationRequired={handleActivationRequired}
                                />
                            </motion.div>
                        ) : activeView === AUTH_VIEWS.REGISTER ? (
                            <motion.div
                                key="register"
                                initial={{ opacity: 0 }}
                                animate={{ opacity: 1 }}
                                exit={{ opacity: 0 }}
                                transition={{ duration: 0.25 }}
                            >
                                <RegisterForm
                                    redirectPath={redirectPath}
                                    onAuthSuccess={handleAuthSuccess}
                                    onSwitchToLogin={() => setActiveView(AUTH_VIEWS.LOGIN)}
                                    onActivationRequired={handleActivationRequired}
                                />
                            </motion.div>
                        ) : activeView === AUTH_VIEWS.ACTIVATE ? (
                            <motion.div
                                key="activate"
                                initial={{ opacity: 0 }}
                                animate={{ opacity: 1 }}
                                exit={{ opacity: 0 }}
                                transition={{ duration: 0.25 }}
                            >
                                <ActivateForm
                                    key={activationEmail}
                                    email={activationEmail}
                                    onAuthSuccess={handleAuthSuccess}
                                    onSwitchToLogin={() => setActiveView(AUTH_VIEWS.LOGIN)}
                                />
                            </motion.div>
                        ) : (
                            <motion.div
                                key="profile"
                                initial={{ opacity: 0 }}
                                animate={{ opacity: 1 }}
                                exit={{ opacity: 0 }}
                                transition={{ duration: 0.25 }}
                            >
                                <ProfileCreateWizard
                                    embedded
                                    onComplete={handleProfileComplete}
                                    onEmbeddedFooter={setWizardFooter}
                                />
                            </motion.div>
                        )}
                    </AnimatePresence>
                </div>
                </div>

                {wizardFooter ? (
                    <div className="shrink-0 border-t border-slate-200/80 bg-white/95 px-6 py-3 md:px-8">
                        <div className="flex items-center justify-between gap-3">
                            <button
                                type="button"
                                className="btn btn-dark-app px-3 py-1.5 text-xs md:px-4 md:py-2 md:text-sm"
                                onClick={wizardFooter.onPrev}
                                disabled={!wizardFooter.canGoBack || wizardFooter.isBusy}
                            >
                                <i className="bi bi-arrow-left me-2" aria-hidden />
                                Back
                            </button>
                            <button
                                type="button"
                                className="btn btn-dark-app px-3 py-1.5 text-xs disabled:cursor-not-allowed disabled:opacity-50 md:px-4 md:py-2 md:text-sm"
                                onClick={wizardFooter.onNext}
                                disabled={wizardFooter.isBusy}
                            >
                                {wizardFooter.submitting ? (
                                    <>
                                        <span
                                            className="spinner-border spinner-border-sm me-2"
                                            role="status"
                                            aria-hidden
                                        />
                                        Saving…
                                    </>
                                ) : wizardFooter.industrySuggesting ? (
                                    <>
                                        <span
                                            className="spinner-border spinner-border-sm me-2"
                                            role="status"
                                            aria-hidden
                                        />
                                        Suggesting…
                                    </>
                                ) : wizardFooter.isLastFormStep ? (
                                    <>
                                        Finish
                                        <i className="bi bi-arrow-right ms-2" aria-hidden />
                                    </>
                                ) : (
                                    <>
                                        Next
                                        <i className="bi bi-arrow-right ms-2" aria-hidden />
                                    </>
                                )}
                            </button>
                        </div>
                    </div>
                ) : null}
            </motion.div>
            </div>
        </div>,
        document.body
    );
}